import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, X, Sparkles, AlertTriangle } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATES = [
  { key: "still_interested", label: "Still Interested?" },
  { key: "documents_needed", label: "Documents Needed" },
  { key: "approved", label: "Approved — Complete Your Booking" },
  { key: "vehicle_available", label: "Vehicle Available Again" },
  { key: "custom", label: "Custom Message" },
];

const daysOld = (iso) => {
  if (!iso) return "?";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d === 0 ? "today" : `${d} day${d === 1 ? "" : "s"} ago`;
};

export default function ReminderDialog({ r, bulkIds, token, onClose, onSent }) {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [tpls, setTpls] = useState(null);
  const [tpl, setTpl] = useState("still_interested");
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [needsOverride, setNeedsOverride] = useState("");
  const [override, setOverride] = useState(false);
  const bulk = !!bulkIds?.length;

  useEffect(() => {
    axios.get(`${API}/reservations/reminder-templates`, H).then(({ data }) => {
      setTpls(data.templates);
      setMsg((data.templates.still_interested || "").replace("{{first_name}}", bulk ? "{{first_name}}" : (r?.name || "there").split(" ")[0]));
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const pickTpl = (k) => {
    setTpl(k);
    const raw = tpls?.[k] || "";
    setMsg(k === "custom" ? "" : raw.replace("{{first_name}}", bulk ? "{{first_name}}" : (r?.name || "there").split(" ")[0]));
  };

  const aiDraft = async () => {
    setDrafting(true);
    try {
      const { data } = await axios.post(`${API}/assistant/draft`, { reservation_id: r.id, channel: "sms" }, H);
      setMsg(data.draft);
      setTpl("custom");
      toast.info("AI draft ready — review and edit before sending.");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI drafting failed."); }
    finally { setDrafting(false); }
  };

  const send = async () => {
    if (!msg.trim()) return toast.error("Message can't be empty.");
    setSending(true);
    try {
      if (bulk) {
        const { data } = await axios.post(`${API}/reservations/bulk`,
          { ids: bulkIds, action: "reminder", message: msg, template: tpl, override }, H);
        toast.success(`Reminders sent to ${data.ok_count} of ${data.total} customers.`);
        const blocked = data.results.filter((x) => !x.ok);
        if (blocked.length) toast.info(`${blocked.length} skipped: ${blocked[0].reason}`);
      } else {
        await axios.post(`${API}/reservations/${r.id}/reminder`, { message: msg, template: tpl, override }, H);
        toast.success(`Reminder sent to ${r.name}.`);
      }
      onSent?.(); onClose();
    } catch (e) {
      if (e?.response?.status === 409) { setNeedsOverride(e.response.data.detail); }
      else toast.error(e?.response?.data?.detail || "Could not send reminder.");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="reminder-dialog">
      <div className="bg-[#0C0C0C] border border-white/15 rounded-xl w-full max-w-lg p-5">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-display font-bold text-white flex items-center gap-2"><Send size={16} className="text-[#F2B705]" /> Send Reminder</h3>
          <button data-testid="reminder-close" onClick={onClose} className="text-white/40 hover:text-white"><X size={17} /></button>
        </div>

        {bulk ? (
          <div className="bg-black/40 border border-white/10 rounded-md p-3 mb-3 text-sm text-white/70">
            Sending to <b className="text-white">{bulkIds.length} selected customers</b>. <span className="text-white/40">{"{{first_name}}"} is personalized for each customer. Opted-out and recently-reminded customers are skipped automatically.</span>
          </div>
        ) : (
          <div className="bg-black/40 border border-white/10 rounded-md p-3 mb-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div><span className="text-white/40">Customer: </span><span className="text-white font-semibold">{r.name}</span></div>
            <div><span className="text-white/40">Phone: </span><span className="text-white/80">{r.phone || "—"}</span></div>
            <div><span className="text-white/40">Vehicle: </span><span className="text-white/80">{r.vehicle || "Any"}</span></div>
            <div><span className="text-white/40">Request age: </span><span className="text-white/80">{daysOld(r.created_at)}</span></div>
            {r.last_reminder_at && <div className="col-span-2 text-[#F2B705]/80">Last reminder: {fmtDateTime(r.last_reminder_at)}</div>}
          </div>
        )}

        <div className="flex flex-wrap gap-1.5 mb-2">
          {TEMPLATES.map((t) => (
            <button key={t.key} data-testid={`reminder-tpl-${t.key}`} onClick={() => pickTpl(t.key)}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${tpl === t.key ? "bg-[#F2B705] text-black border-[#F2B705] font-bold" : "bg-white/5 border-white/15 text-white/60 hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <textarea data-testid="reminder-message" rows={4} value={msg} onChange={(e) => setMsg(e.target.value)}
          className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-white text-sm"
          placeholder="Type your message…" />
        <div className="flex items-center justify-between text-[11px] text-white/35 mt-1">
          <span>{msg.length} chars {msg.length > 320 && <span className="text-orange-300">— long for SMS</span>}</span>
          {!bulk && (
            <button data-testid="reminder-ai-draft" onClick={aiDraft} disabled={drafting}
              className="flex items-center gap-1 text-[#F2B705] hover:text-white disabled:opacity-50">
              {drafting ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} AI Draft (you approve before send)
            </button>
          )}
        </div>

        {needsOverride && (
          <div className="mt-2 bg-orange-500/10 border border-orange-500/30 rounded-md p-2.5 text-xs text-orange-200" data-testid="reminder-override-warning">
            <AlertTriangle size={12} className="inline mr-1" /> {needsOverride}
            <label className="flex items-center gap-2 mt-2 cursor-pointer text-white/80">
              <input type="checkbox" data-testid="reminder-override-check" checked={override} onChange={(e) => setOverride(e.target.checked)} className="accent-[#F2B705]" />
              Send anyway (manual override)
            </label>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-white/15 text-white/60 text-sm">Cancel</button>
          <button data-testid="reminder-send-btn" onClick={send} disabled={sending || (!!needsOverride && !override)}
            className="flex items-center gap-1.5 px-5 py-2 rounded-md bg-[#F2B705] text-black font-bold text-sm disabled:opacity-40">
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} {bulk ? `Send to ${bulkIds.length} Customers` : "Confirm & Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
